// Gizmo System for Scene Editor
import { System } from '@/engine/systems/System';
import { logger } from '@/engine/logging';
import { editorMode, sceneState, updateObject } from '../stores/sceneEditorStore';
import { TransformGizmo } from '../components/TransformGizmo';
import type { SceneEditorMode } from '../types';
import * as PIXI from 'pixi.js';

export class GizmoSystem extends System {
  private viewport?: PIXI.Container;
  private stage?: PIXI.Container;
  private gizmo?: TransformGizmo;
  private gizmoContainer?: PIXI.Container;

  constructor(scene: any) {
    super(scene);
  }

  /**
   * Инициализация системы с необходимыми зависимостями
   */
  initialize(viewport: PIXI.Container, stage: PIXI.Container) {
    this.viewport = viewport;
    this.stage = stage;
    this.createGizmo();

    logger.info('🎯 Gizmo System initialized', {
      source: 'gizmo-system',
      viewport: !!this.viewport,
      stage: !!this.stage,
      gizmo: !!this.gizmo,
    });
  }

  protected onStart(): void {
    // Система уже инициализирована в initialize()
  }

  protected onStop(): void {
    this.destroyGizmo();
  }

  protected onUpdate(_deltaTime: number): void {
    // Обновление не требуется для gizmo
  }

  protected onDestroy(): void {
    this.destroyGizmo();
  }

  private createGizmo() {
    if (!this.viewport || !this.stage) {
      logger.warn('⚠️ Cannot create gizmo - viewport or stage not available', {
        source: 'gizmo-system',
      });
      return;
    }

    // Создаем контейнер для gizmo поверх всех объектов
    this.gizmoContainer = new PIXI.Container();
    this.gizmoContainer.zIndex = 1000; // Поверх всех объектов
    this.gizmoContainer.eventMode = 'static'; // PIXI 8: Важно для событий мыши
    this.gizmoContainer.interactiveChildren = true;
    this.viewport.addChild(this.gizmoContainer);

    // Создаем gizmo с передачей stage и viewport для правильной трансформации координат
    this.gizmo = new TransformGizmo(this.gizmoContainer, this.stage, this.viewport, {
      axisLength: 60,
      axisWidth: 3,
      handleSize: 12,
      scaleBoxSize: 10,
    });

    // Подписываемся на изменение режима редактора
    editorMode.subscribe(mode => {
      if (this.gizmo) {
        this.gizmo.setMode(mode);
        logger.info('🎯 Gizmo mode changed', {
          source: 'gizmo-system',
          mode,
        });
      }
    });

    // Настраиваем обработчики трансформации
    this.gizmo.setOnTransform((type, data) => {
      this.handleGizmoTransform(type, data);
    });

    // Скрываем gizmo по умолчанию
    this.gizmo.hide();

    logger.info('🎯 Transform gizmo created', {
      source: 'gizmo-system',
      gizmo: !!this.gizmo,
      gizmoContainer: !!this.gizmoContainer,
      viewportChildren: this.viewport.children.length,
      gizmoContainerInteractive: this.gizmoContainer.interactive,
    });
  }

  private destroyGizmo() {
    this.gizmo?.destroy();
    this.gizmo = undefined;
    this.gizmoContainer?.destroy();
    this.gizmoContainer = undefined;
  }

  /**
   * Обновить gizmo для выделенного объекта
   */
  updateGizmoForSelectedObject() {
    if (!this.gizmo) {
      logger.warn('⚠️ Gizmo not initialized', { source: 'gizmo-system' });
      return;
    }

    const state = sceneState.get();
    const selectedObject = state.selectedObjectId ? state.objects[state.selectedObjectId] : null;

    if (selectedObject && selectedObject.type === 'sprite') {
      // Показываем gizmo для выделенного объекта
      this.gizmo.show({
        x: selectedObject.x,
        y: selectedObject.y,
        scale: selectedObject.scale,
        rotation: selectedObject.rotation,
        width: 100, // Можно получить из текстуры
        height: 100,
      });

      // Обновляем позицию gizmo
      this.gizmo.updatePosition(selectedObject.x, selectedObject.y);

      // Устанавливаем режим gizmo
      const currentMode = editorMode.get();
      this.gizmo.setMode(currentMode);

      logger.info('🎯 Gizmo updated for selected object', {
        source: 'gizmo-system',
        objectId: selectedObject.id,
        position: { x: selectedObject.x, y: selectedObject.y },
        mode: currentMode,
        gizmoVisible: this.gizmoContainer?.visible,
      });
    } else {
      // Скрываем gizmo если нет выделенного объекта
      this.gizmo.hide();
      logger.info('🎯 Gizmo hidden - no selected object', {
        source: 'gizmo-system',
        selectedObjectId: state.selectedObjectId,
        objectsCount: Object.keys(state.objects).length,
      });
    }
  }

  /**
   * Обновить режим gizmo
   */
  updateGizmoMode(mode: SceneEditorMode) {
    if (this.gizmo) {
      this.gizmo.setMode(mode);
    }
  }

  /**
   * Получить контейнер gizmo для передачи событий
   */
  getGizmoContainer(): PIXI.Container | undefined {
    return this.gizmoContainer;
  }

  private handleGizmoTransform(type: 'move' | 'scale' | 'rotate', data: any) {
    const state = sceneState.get();
    const selectedObject = state.selectedObjectId ? state.objects[state.selectedObjectId] : null;

    if (!selectedObject) return;

    // Delta уже в правильных локальных координатах viewport (toLocal учел все трансформации)
    const deltaX = data.deltaX;
    const deltaY = data.deltaY;

    switch (type) {
      case 'move':
        this.updateObjectPosition(
          selectedObject.id,
          selectedObject.x + deltaX,
          selectedObject.y + deltaY
        );
        break;
      case 'scale':
        // Масштабирование с учетом направления handle
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        // Определяем знак (увеличение или уменьшение) по направлению от центра
        const sign = deltaX + deltaY > 0 ? 1 : -1;
        const scaleDelta = (distance * sign) / 200; // Делитель для чувствительности
        const newScale = Math.max(0.1, Math.min(10, selectedObject.scale + scaleDelta));
        this.updateObjectScale(selectedObject.id, newScale);
        break;
      case 'rotate':
        // Вращение относительно центра объекта
        const angle = Math.atan2(deltaY, deltaX);
        const angleDelta = angle / 50; // Делитель для чувствительности
        const newRotation = selectedObject.rotation + angleDelta;
        this.updateObjectRotation(selectedObject.id, newRotation);
        break;
    }

    logger.info('🎯 Gizmo transform applied', {
      source: 'gizmo-system',
      type,
      objectId: selectedObject.id,
      delta: { x: deltaX, y: deltaY },
    });
  }

  private updateObjectPosition(objectId: string, x: number, y: number) {
    const state = sceneState.get();
    const object = state.objects[objectId];
    if (!object) return;

    // Apply snap to grid if enabled
    let snappedX = x;
    let snappedY = y;

    if (state.snap.enabled && state.grid.enabled) {
      const gridSize = state.grid.size;
      snappedX = Math.round(x / gridSize) * gridSize;
      snappedY = Math.round(y / gridSize) * gridSize;
    }

    // Update through store
    updateObject(objectId, { x: snappedX, y: snappedY });
  }

  private updateObjectScale(objectId: string, scale: number) {
    const state = sceneState.get();
    const object = state.objects[objectId];
    if (!object) return;

    updateObject(objectId, { scale });
  }

  private updateObjectRotation(objectId: string, rotation: number) {
    const state = sceneState.get();
    const object = state.objects[objectId];
    if (!object) return;

    updateObject(objectId, { rotation });
  }
}
